export const getYandexAddressUrl = (address) => (
  address ? `https://yandex.ru/maps/?text=${encodeURIComponent(address)}` : null
);

export const getYandexMapsUrl = (address) => (
  address ? `yandexmaps://maps.yandex.ru/?text=${encodeURIComponent(address)}` : null
);

export const getYandexNavigatorUrl = (address) => (
  address ? `yandexnavi://search?text=${encodeURIComponent(address)}` : null
);

export const YANDEX_MAPS_ROUTE_POINTS_LIMIT = 30;

const normalizeRouteCoordinate = (value, min, max) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null;
};

export const getYandexRoutePoints = (items = []) => (
  (Array.isArray(items) ? items : []).reduce((points, item) => {
    const latitude = normalizeRouteCoordinate(item?.latitude, -90, 90);
    const longitude = normalizeRouteCoordinate(item?.longitude, -180, 180);
    if (latitude !== null && longitude !== null) {
      points.push({ latitude, longitude });
    }
    return points;
  }, [])
);

export const getYandexRouteUrl = (points = []) => {
  const normalizedPoints = getYandexRoutePoints(points);
  if (normalizedPoints.length < 2) return null;

  const routeText = normalizedPoints
    .map(({ latitude, longitude }) => `${latitude},${longitude}`)
    .join('~');
  return `https://yandex.ru/maps?rtext=${encodeURIComponent(routeText)}&rtt=auto`;
};

export const getYandexNavigatorRouteUrl = (points = []) => {
  const normalizedPoints = getYandexRoutePoints(points);
  if (normalizedPoints.length < 2) return null;

  const [from, ...rest] = normalizedPoints;
  const to = rest[rest.length - 1];
  const via = rest.slice(0, -1);
  const params = new URLSearchParams({
    lat_from: String(from.latitude),
    lon_from: String(from.longitude),
    lat_to: String(to.latitude),
    lon_to: String(to.longitude)
  });
  via.forEach((point, index) => {
    params.set(`lat_via_${index}`, String(point.latitude));
    params.set(`lon_via_${index}`, String(point.longitude));
  });
  return `yandexnavi://build_route_on_map?${params.toString()}`;
};

const YANDEX_V3_OPEN_MAPS_BUTTON_SELECTOR = '.ymaps3--open-maps-button';

export const suppressYandexOpenMapsButton = (container) => {
  if (!(container instanceof Element)) {
    return () => {};
  }

  const hideOpenMapsButton = () => {
    container.querySelectorAll(YANDEX_V3_OPEN_MAPS_BUTTON_SELECTOR).forEach((button) => {
      const control = button.closest('.ymaps3--control');
      const target = control || button;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      target.style.display = 'none';
      target.setAttribute('aria-hidden', 'true');
    });
  };

  hideOpenMapsButton();

  const observer = new MutationObserver(() => {
    hideOpenMapsButton();
  });

  observer.observe(container, {
    childList: true,
    subtree: true
  });

  return () => observer.disconnect();
};

export const loadYandexMaps = (apiKey) => new Promise((resolve, reject) => {
  const rejectLoad = () => reject(new Error('Не удалось загрузить API Яндекс.Карт'));

  const waitUntilReady = () => {
    if (!window.ymaps || typeof window.ymaps.ready !== 'function') {
      reject(new Error('API Яндекс.Карт не загрузился'));
      return;
    }

    window.ymaps.ready(() => {
      if (typeof window.ymaps.Map === 'function') {
        resolve(window.ymaps);
      } else {
        reject(new Error('API Яндекс.Карт загрузился не полностью'));
      }
    });
  };

  if (window.ymaps && typeof window.ymaps.Map === 'function') {
    waitUntilReady();
    return;
  }

  const existingScript = document.getElementById('yandex-maps-api-v2');
  if (existingScript) {
    if (existingScript.dataset.status === 'error') {
      existingScript.remove();
    } else if (existingScript.dataset.status === 'loaded') {
      waitUntilReady();
      return;
    } else {
      existingScript.addEventListener('load', waitUntilReady, { once: true });
      existingScript.addEventListener('error', rejectLoad, { once: true });
      return;
    }
  }

  const script = document.createElement('script');
  script.id = 'yandex-maps-api-v2';
  script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU&load=package.full`;
  script.async = true;
  script.dataset.status = 'loading';
  script.onload = () => {
    script.dataset.status = 'loaded';
    waitUntilReady();
  };
  script.onerror = () => {
    script.dataset.status = 'error';
    rejectLoad();
  };
  document.head.appendChild(script);
});

export const loadYandexMapsV3 = (() => {
  let loaderPromise = null;

  return (apiKey) => {
    if (loaderPromise) {
      return loaderPromise;
    }

    loaderPromise = new Promise((resolve, reject) => {
      const rejectLoad = () => reject(new Error('Не удалось загрузить API Яндекс.Карт v3. Проверьте валидность API-ключа в кабинете Яндекс.Карт.'));

      const waitUntilReady = async () => {
        if (!window.ymaps3 || !window.ymaps3.ready) {
          reject(new Error('API Яндекс.Карт v3 не загрузился'));
          return;
        }

        try {
          await window.ymaps3.ready;
          if (window.ymaps3.import?.registerCdn) {
            const origin = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
            if (!window.__ymaps3DefaultUiRegistered) {
              if (origin) {
                window.ymaps3.import.registerCdn(`${origin}/vendor/ymaps3/{package}`, [
                  '@yandex/ymaps3-default-ui-theme@0.0'
                ]);
              }
              window.ymaps3.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
                '@yandex/ymaps3-default-ui-theme@0.0'
              ]);
              window.__ymaps3DefaultUiRegistered = true;
            }

            if (!window.__ymaps3ClustererRegistered) {
              if (origin) {
                window.ymaps3.import.registerCdn(`${origin}/vendor/ymaps3/{package}`, [
                  '@yandex/ymaps3-clusterer@0.0'
                ]);
              }
              window.ymaps3.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
                '@yandex/ymaps3-clusterer@0.0'
              ]);
              window.__ymaps3ClustererRegistered = true;
            }
          }

          const defaultUiTheme = await window.ymaps3.import('@yandex/ymaps3-default-ui-theme');
          resolve({
            ymaps3: window.ymaps3,
            defaultUiTheme
          });
        } catch (error) {
          reject(error instanceof Error ? error : new Error('Не удалось подготовить API Яндекс.Карт v3'));
        }
      };

      if (window.ymaps3?.YMap) {
        waitUntilReady();
        return;
      }

      const existingScript = document.getElementById('yandex-maps-api-v3');
      if (existingScript) {
        if (existingScript.dataset.status === 'error') {
          existingScript.remove();
        } else if (existingScript.dataset.status === 'loaded') {
          waitUntilReady();
          return;
        } else {
          existingScript.addEventListener('load', waitUntilReady, { once: true });
          existingScript.addEventListener('error', rejectLoad, { once: true });
          return;
        }
      }

      const script = document.createElement('script');
      script.id = 'yandex-maps-api-v3';
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
      script.async = true;
      script.dataset.status = 'loading';
      script.onload = () => {
        script.dataset.status = 'loaded';
        waitUntilReady();
      };
      script.onerror = () => {
        script.dataset.status = 'error';
        rejectLoad();
      };
      document.head.appendChild(script);
    }).catch((error) => {
      loaderPromise = null;
      throw error;
    });

    return loaderPromise;
  };
})();
